import {
  APP_INITIALIZER,
  Injectable,
  OnDestroy,
  Provider,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { FetchEvent, FormStateService } from '../formly/form-state.service';
import { AnalyticsService } from './analytics.service';
import { resolveFetchEventName } from './fetch-event-names';

/**
 * Root service that turns lookup outcomes (emitted by AnalyticsAwareFieldType
 * via FormStateService.fetchEvent$) into Mixpanel events.
 *
 * It maps the field type to an event name (failures get `_ERROR`), enriches with
 * the current service/institution context, and dispatches through AnalyticsService
 * — which portals bind to their MixpanelService via `{ provide: AnalyticsService,
 * useClass: MixpanelService }`. No gate here; the base class already filtered.
 *
 * Construct it eagerly with `provideFetchAnalyticsForwarder()`; a providedIn:'root'
 * service is otherwise never instantiated (nothing injects it) and would never
 * subscribe.
 */
@Injectable({ providedIn: 'root' })
export class FetchAnalyticsForwarder implements OnDestroy {
  private readonly subscription: Subscription;

  constructor(
    private readonly formStateService: FormStateService,
    private readonly analyticsService: AnalyticsService
  ) {
    this.subscription = this.formStateService.fetchEvent$.subscribe(event =>
      this.onFetchEvent(event)
    );
  }

  private onFetchEvent(event: FetchEvent): void {
    const ctx = this.formStateService.getServiceContext();
    this.analyticsService.trackEvent({
      eventType: resolveFetchEventName(event.fieldType, event.outcome),
      service_name: ctx.service,
      service_id: ctx.serviceId,
      service_code: ctx.serviceCode,
      institution_id: ctx.institutionId,
      institution_name: ctx.institutionName,
      institution_acronym: ctx.institutionAcronym,
      ...(event.outcome === 'failure'
        ? {
            errorMessage: event.errorMessage,
            error_Message: event.errorMessage,
            errorCode: event.errorCode,
          }
        : {}),
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}

/**
 * Spread into a portal's `providers` to construct the forwarder at startup so it
 * subscribes before any lookup runs. Pair it with the portal's
 * `{ provide: AnalyticsService, useClass: MixpanelService }` binding.
 */
export function provideFetchAnalyticsForwarder(): Provider[] {
  return [
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => () => undefined,
      deps: [FetchAnalyticsForwarder],
    },
  ];
}
